import Lara from '@primeuix/themes/lara';

// Preserve Lara's widths when they already exceed the touch target. Component
// variables are set on their owner so body overlays and scoped presets inherit
// the same policy without changing fonts, icons, or the desktop size variants.
const dropdownWidths = (component, target) => {
  const { dropdown } = Lara.components[component];

  return [
    `--p-${component}-dropdown-width: max(${dropdown.width}, ${target});`,
    ...['sm', 'lg']
      .filter((size) => dropdown[size])
      .map(
        (size) =>
          `--p-${component}-dropdown-${size}-width: max(${dropdown[size].width}, ${target});`,
      ),
  ].join('\n    ');
};

export const touchControlStyles = ({ dt }) => {
  const target = dt('bayhive.touch.targetSize');

  return `
/* Touch-first phones and tablets use one minimum target at every viewport
   width. A narrow mouse/trackpad window retains Lara's normal size variants. */
@media (pointer: coarse) {
  html[data-ui-brand='bayhive'] {
    --p-bayhive-touch-min-size: ${target};
  }

  /* PrimeVue has width tokens for attached actions, but no general control
     min-height token. Minimums leave multiline and already-large controls free
     to grow and preserve small/default/large typography. */
  html[data-ui-brand='bayhive'] :is(
    .p-button,
    .p-inputtext,
    .p-textarea,
    .p-autocomplete,
    .p-autocomplete-input-multiple,
    .p-select,
    .p-multiselect,
    .p-treeselect,
    .p-datepicker,
    .p-inputnumber,
    .p-password,
    .p-togglebutton,
    .p-autocomplete-dropdown,
    .p-datepicker-dropdown,
    .p-splitbutton-dropdown
  ) {
    min-block-size: ${target};
  }

  /* Text inputs center their own single-line content. Select labels are block
     elements, so center those within the taller target while preserving their
     truncation, custom value slots and full-height dropdown buttons. */
  html[data-ui-brand='bayhive'] :is(
    .p-select-label:not(input),
    .p-multiselect-label-container,
    .p-treeselect-label-container
  ) {
    align-self: center;
  }

  /* Let fluid dropdowns shrink inside form grids instead of letting a long
     selected label widen the field beyond the phone viewport. */
  html[data-ui-brand='bayhive'] :is(
    .p-select,
    .p-multiselect,
    .p-treeselect,
    .bayhive-multiselect-search
  ) {
    min-inline-size: 0;
  }

  html[data-ui-brand='bayhive'] :is(
    .p-button-icon-only,
    .p-splitbutton-dropdown,
    .p-autocomplete-dropdown,
    .p-datepicker-dropdown
  ) {
    min-inline-size: ${target};
    flex-shrink: 0;
  }

  html[data-ui-brand='bayhive'] .p-autocomplete {
    ${dropdownWidths('autocomplete', target)}
  }

  html[data-ui-brand='bayhive'] .p-datepicker {
    ${dropdownWidths('datepicker', target)}
    --p-bayhive-touch-date-icon-size: ${dt('icon.size')};
  }

  html[data-ui-brand='bayhive'] .p-datepicker:has(.p-inputtext-sm) {
    --p-bayhive-touch-date-icon-size: ${dt('form.field.sm.font.size')};
  }

  html[data-ui-brand='bayhive'] .p-datepicker:has(.p-inputtext-lg) {
    --p-bayhive-touch-date-icon-size: ${dt('form.field.lg.font.size')};
  }

  /* With iconDisplay="input", the click listener is on the glyph itself.
     Grow that element's box while preserving its glyph and the text inset. */
  html[data-ui-brand='bayhive'] .p-datepicker-input-icon-container {
    block-size: ${target};
    inline-size: ${target};
    inset-inline-end: 0;
    margin-block-start: 0;
    transform: translateY(-50%);
  }

  html[data-ui-brand='bayhive'] .p-datepicker-input-icon-container > :is(.p-datepicker-input-icon, .bayhive-datepicker-input-icon) {
    align-items: center;
    block-size: 100%;
    display: inline-flex;
    inline-size: 100%;
    justify-content: center;
  }

  html[data-ui-brand='bayhive'] .p-datepicker-input-icon-container > svg.p-datepicker-input-icon {
    box-sizing: border-box;
    padding: calc((${target} - var(--p-bayhive-touch-date-icon-size)) / 2);
  }

  html[data-ui-brand='bayhive'] .p-datepicker:has(.p-datepicker-input-icon-container) .p-datepicker-input {
    padding-inline-end: calc(${target} + ${dt('form.field.padding.x')});
  }

  html[data-ui-brand='bayhive'] .p-datepicker:has(.p-datepicker-input-icon-container) .p-datepicker-clear-icon {
    inset-inline-end: calc(${target} + ${dt('form.field.padding.x')});
  }

  html[data-ui-brand='bayhive'] .p-datepicker:has(.p-datepicker-input-icon-container):has(.p-datepicker-clear-icon) .p-datepicker-input {
    padding-inline-end: calc(${target} + ${dt('icon.size')} + 2 * ${dt('form.field.padding.x')});
  }

  html[data-ui-brand='bayhive'] .p-select {
    ${dropdownWidths('select', target)}
  }

  html[data-ui-brand='bayhive'] .p-multiselect {
    ${dropdownWidths('multiselect', target)}
  }

  html[data-ui-brand='bayhive'] .p-treeselect {
    ${dropdownWidths('treeselect', target)}
  }

  html[data-ui-brand='bayhive'] .p-inputnumber {
    --p-inputnumber-button-width: max(${Lara.components.inputnumber.button.width}, ${target});
  }

  html[data-ui-brand='bayhive'] :is(.p-inputnumber-horizontal, .p-inputnumber-vertical) .p-inputnumber-button {
    min-block-size: ${target};
    min-inline-size: ${target};
  }

  html[data-ui-brand='bayhive'] .p-paginator {
    --p-paginator-nav-button-width: max(${Lara.components.paginator.navButton.width}, ${target});
    --p-paginator-nav-button-height: max(${Lara.components.paginator.navButton.height}, ${target});
  }

  html[data-ui-brand='bayhive'] :is(
    .p-autocomplete-option,
    .p-select-option,
    .p-multiselect-option,
    .p-listbox-option,
    .p-menu-item-link,
    .p-tieredmenu-item-link,
    .p-menubar-item-link,
    .bayhive-field-overlay-option-row--header
  ) {
    min-block-size: ${target};
  }

  html[data-ui-brand='bayhive'] .bayhive-touch-picker-overlay :is(
    .p-select-option,
    .p-multiselect-option,
    .p-select-option-label,
    .p-multiselect-option-label
  ) {
    min-inline-size: 0;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  html[data-ui-brand='bayhive'] .bayhive-touch-picker-overlay .p-virtualscroller-content {
    box-sizing: border-box;
    inline-size: 100%;
    max-inline-size: 100%;
  }

  html[data-ui-brand='bayhive'] .bayhive-touch-picker-overlay .p-virtualscroller {
    max-block-size: inherit;
  }

  /* These are wrapping compositions. The RO's compact inline action bar owns
     its separate grouping and available space for financial totals. */
  html[data-ui-brand='bayhive'] :is(
    .bayhive-form-actions,
    .shop-management-page-toolbar__main,
    .shop-management-page-toolbar__actions,
    .p-paginator-pages
  ) {
    flex-wrap: wrap;
  }
}
`;
};

export default touchControlStyles;
